import {
  basePlatform,
  platformLabel,
  useEnv,
} from '@sparklayer-io/spark-dash-core/context/env-context';
import { type SyncLogIdentifier, SyncLogStatusAlert } from '@sparklayer-io/spark-dash-pages';
import { PLATFORM_PATH } from '@sparklayer-io/spark-dash-pages/constants/paths';
import { useHandlers } from '@sparklayer-io/spark-dash-pages/context/page-context';
import { Accordion, Button, LearnMoreLink, Text } from '@ui/themed';
import { t } from 'i18next';
import type { I18nPrefix } from '../../../../i18n/prefix';
import { DOCS_URLS } from '../../constants';
import { onboardingStepBadge } from './onboarding-step-badge';
import { OnboardingStepIcon } from './onboarding-step-icon';

const COMPONENT = 'features:home.components.onboarding.connect' satisfies I18nPrefix;

export function ConnectStep({
  isExpanded,
  onExpandedChange,
}: {
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}) {
  const env = useEnv();
  const { navigationHandler } = useHandlers();
  const complete = env.onboarding_platform;
  const platformName = platformLabel(env.connected_platform) || t(`${COMPONENT}.platform_fallback`);

  const integration = basePlatform(env.platform) ?? '';
  const syncFor = (dataType: SyncLogIdentifier['dataType']): SyncLogIdentifier => ({
    integration,
    dataType,
  });

  return (
    <Accordion.Item
      title={t(`${COMPONENT}.title`, { platform: platformName })}
      subtitle={t(complete ? `${COMPONENT}.subtitle_complete` : `${COMPONENT}.subtitle_incomplete`)}
      icon={<OnboardingStepIcon complete={complete} />}
      badge={onboardingStepBadge(complete)}
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
    >
      {!complete ? (
        <>
          <Text variant="secondary">{t(`${COMPONENT}.not_connected.description`)}</Text>
          <div>
            <Button variant="primary" onClick={() => navigationHandler.navigate(PLATFORM_PATH)}>
              {t(`${COMPONENT}.not_connected.action`)}
            </Button>
          </div>
        </>
      ) : (
        <>
          <Text variant="secondary">
            {t(`${COMPONENT}.connected.description`)}{' '}
            <LearnMoreLink link={DOCS_URLS.dashboardConfigurations} />
          </Text>
          <div className="flex flex-col gap-3">
            <SyncLogStatusAlert syncFor={syncFor('products')} allowViewErrors />
            <SyncLogStatusAlert syncFor={syncFor('customers')} allowViewErrors />
          </div>
          <Text variant="secondary" size="sm">
            {t(`${COMPONENT}.connected.sync_note`)}
          </Text>
          <div>
            <Button variant="secondary" onClick={() => navigationHandler.navigate(PLATFORM_PATH)}>
              {t(`${COMPONENT}.connected.action`)}
            </Button>
          </div>
        </>
      )}
    </Accordion.Item>
  );
}
